import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';

interface NavbarProps {
  user?: {
    id: string;
    email: string;
    fullName: string;
    role: string;
  } | null;
  onLogout: () => void;
  isPreviewMode?: boolean;
  invitationEmail?: string;
}

export function Navbar({ user, onLogout, isPreviewMode = false, invitationEmail }: NavbarProps) {
  const location = useLocation();
  const [logoSrc, setLogoSrc] = useState('/logo-gold-small.png');

  useEffect(() => {
    const updateLogo = () => {
      if (window.innerWidth <= 768) {
        // Mobile: recberry_gold_r.png
        setLogoSrc('/recberry_gold_r.png');
      } else {
        // Desktop/Tablet: logo-gold-small.png
        setLogoSrc('/logo-gold-small.png');
      }
    };

    updateLogo();
    window.addEventListener('resize', updateLogo);
    return () => window.removeEventListener('resize', updateLogo);
  }, []);
  

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  return (
    <>
      <style>{styles}</style>
      <nav id="navbar">
        <div className="navbar-container">
        <div className="logo">
          <Link to={isPreviewMode && window.location.search ? `/${window.location.search}` : "/"}>
            <img 
              src={logoSrc} 
              alt="recberry" 
              className="logo-image"
            />
          </Link>
        </div>
        
        <div className="menu">
          {isPreviewMode ? (
            // Simple menu for invitation users
            <>
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'active' : ''}
              >
                COURSE OVERVIEW
              </Link>
              <Link 
                to="/module1" 
                className={location.pathname === '/module1' ? 'active' : ''}
              >
                PREVIEW MODULE 1
              </Link>
              <button
                onClick={() => {
                  // Scroll to interest form or show modal
                  const event = new CustomEvent('showInterestForm', {
                    detail: { formType: 'request-access' }
                  });
                  window.dispatchEvent(event);
                }}
                style={{
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                GET FULL ACCESS
              </button>
            </>
          ) : user ? (
            // Full menu for authenticated users
            <>
              <Link 
                to="/dashboard" 
                className={location.pathname === '/dashboard' ? 'active' : ''}
              >
                DASHBOARD
              </Link>
              <Link 
                to="/progress" 
                className={location.pathname === '/progress' ? 'active' : ''}
              >
                PROGRESS
              </Link>
              <Link 
                to="/resources" 
                className={location.pathname === '/resources' ? 'active' : ''}
              >
                RESOURCES
              </Link>
              <Link 
                to="/account" 
                className={location.pathname === '/account' ? 'active' : ''}
              >
                ACCOUNT
              </Link>
              {user.role === 'admin' && (
                <Link 
                  to="/admin" 
                  className={location.pathname === '/admin' ? 'active' : ''}
                >
                  ADMIN
                </Link>
              )}
            </>
          ) : (
            // Menu for non-authenticated users (no invitation)
            <Link 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
            >
              HOME
            </Link>
          )}
          
          {isPreviewMode ? (
            <div className="user-section">
              <div className="user-info">
                <User className="user-icon" />
                <span className="user-name">
                  {invitationEmail ? invitationEmail.split('@')[0] : 'Invited User'}
                </span>
              </div>
              <button
                onClick={() => {
                  const event = new CustomEvent('showInterestForm', {
                    detail: { formType: 'request-access' }
                  });
                  window.dispatchEvent(event);
                }}
                className="login-button"
                style={{
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                START FREE COURSE
              </button>
            </div>
          ) : user ? (
            <div className="user-section">
              <div className="user-info">
                <User className="user-icon" />
                <span className="user-name">
                  {user.fullName || user.email.split('@')[0]}
                </span>
              </div>
              <button onClick={handleLogout} className="logout-button">
                <LogOut className="logout-icon" />
                LOG OUT
              </button>
            </div>
          ) : (
            <Link to="/auth" className="login-button">
              LOG IN
            </Link>
          )}
        </div>
        </div>
      </nav>
    </>
  );
}

// CSS for the new user section (add this to your index.css)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = `
.navbar-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex !important;
  justify-content: space-between;
  align-items: center !important;
  height: 80px !important;
  line-height: 80px !important;
}

#navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--dark);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo {
  display: flex !important;
  align-items: center !important;
  justify-content: center;
  height: 80px !important;
  line-height: 80px !important;
}

.logo a {
  display: flex !important;
  align-items: center !important;
  justify-content: center;
  text-decoration: none;
  height: 100% !important;
  line-height: 80px !important;
}

.logo-image {
  max-width: 180px;
  max-height: 180px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.menu {
  display: flex !important;
  align-items: center !important;
  justify-content: center;
  gap: 2rem;
  height: 80px !important;
  line-height: 80px !important;
}

.menu a {
  color: var(--white);
  text-decoration: none;
  font-family: var(--montserrat);
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.3s ease;
}

.menu a:hover,
.menu a.active {
  color: var(--orange);
}

.user-section {
  display: flex !important;
  align-items: center !important;
  justify-content: center;
  gap: 1rem;
  height: 80px !important;
  line-height: 80px !important;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--white);
  font-family: var(--montserrat);
  font-size: 0.875rem;
  font-weight: 500;
}

.user-icon {
  width: 16px;
  height: 16px;
  color: var(--orange);
}

.user-name {
  color: var(--white);
}

.logout-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--orange);
  color: var(--dark);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: var(--montserrat);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logout-button:hover {
  background: var(--dark-blue);
  color: var(--white);
}

.logout-icon {
  width: 16px;
  height: 16px;
}

.login-button {
  background: var(--orange);
  color: var(--dark);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: var(--montserrat);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.login-button:hover {
  background: var(--dark-blue);
  color: var(--white);
}

.preview-mode-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  line-height: 80px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .navbar-container {
    padding: 0 15px;
  }
  
  .menu {
    gap: 1rem;
  }
  
  .menu a,
  .logout-button,
  .login-button {
    font-size: 0.75rem;
    padding: 0.5rem 1rem;
  }
  
  .user-info {
    display: none; /* Hide user info on mobile */
  }
  
  .logo a {
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .menu a {
    display: none; /* Hide navigation links on very small screens */
  }
  
  .menu a.active {
    display: block; /* Show only active link */
  }
}
`;

export default Navbar;